import { activeCartsQueryOptions, selectCartProducts, useAllCarts } from "@/api/cache/cartQueryOptions";

import {
  AnalyticsTrackingEvent,
  CheckoutDrawer,
  isSubscriptionLine,
  ShoppingCartItem,
  SubscriptionFrequency,
  UserRetailerShoppingCart,
  brandclubStyles,
} from "@brandclub/common-ui";
import { Drawer, SxProps, Theme, useTheme } from "@mui/material";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  BAG_EMPTY,
  CHECKOUT_NOT_STARTED,
  requestCheckoutStart,
  useCheckoutStart,
  type CheckoutStartOutcome,
} from "./checkoutStart";

import { useCartUpdates } from "@/api/cache/cartQueryOptions";
import { createShareableShoppingCart } from "@/api/rest/checkout";
import { useSubscribeAndSaveOffers } from "@/api/cache/subscribeAndSaveQueryOptions";
import { CHECKOUT_SIMPLIFIED_FLAG, pickCheckoutPath } from "@brandclub/common-ui";
import { resolveCheckoutSplitAppId } from "@/modules/checkoutSplit";
import { StoreBrandingType } from "@/redux/types";
import { create } from "zustand";
import { useTrackActions } from "../../hooks/useTracking";
import { useBCRouting } from "../../providers/BCRoutingContextProvider";
import { HOST_DOMAIN, APPID } from "../../utils/domain";
import { CartProductLoading } from "./CartProduct";
import { useSplitTreatments } from "@splitsoftware/splitio-react";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useDomainConfig } from "@/api/cache/appConfigQueryOptions";

interface DialogState {
  open: boolean;
  /** Why the last add was refused. The drawer shows it at the top until the shopper dismisses it. */
  refusal: string | null;
  close: () => void;
}

const useDialogStore = create<DialogState>((set) => ({
  open: false,
  refusal: null,
  close: () => set({ open: false, refusal: null }),
}));

/** The refusal line at the top of the drawer. Plain styles: this file adds no new MUI. */
const RefusalLine = ({ message, onDismiss }: { message: string; onDismiss: () => void }) => (
  <div
    role="alert"
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      margin: "16px 16px 0",
      padding: "12px 16px",
      border: "1px solid #f5c2c0",
      borderRadius: 8,
      background: "#fdf2f2",
      color: "#b42318",
      fontSize: 14,
      lineHeight: 1.4,
    }}
  >
    <span style={{ flex: 1 }}>{message}</span>
    <button
      type="button"
      aria-label="Dismiss"
      onClick={onDismiss}
      style={{ background: "none", border: "none", padding: 0, color: "inherit", fontSize: 18, lineHeight: 1, cursor: "pointer" }}
    >
      ×
    </button>
  </div>
);

export const BagDrawer = () => {
  const theme = useTheme();

  const { data: domainConfig } = useDomainConfig();
  const { data: userProfile } = useUserProfile();
  const { treatments } = useSplitTreatments({
    names: ["brandclub_checkout_free_shipping_offer", CHECKOUT_SIMPLIFIED_FLAG],
    attributes: {
      email: userProfile?.email ?? "",
      // The brand's identity, never this copy's URL — see `checkoutSplit.ts`.
      appId: resolveCheckoutSplitAppId(domainConfig, APPID),
    },
  });
  const bcRouting = useBCRouting();
  const queryClient = useQueryClient();
  const { open, refusal, close } = useDialogStore();
  const { data: allCarts, isLoading: initializingCart } = useAllCarts({
    select: (data) =>
      data.userRetailerShoppingCarts.filter(
        (c) => !!c.items?.length && !!c.retailerId
      ),
    // prevent race conditions when adding an item to the cart triggered this component to mount
    // a premature refetch could cause the new updated cart to be overwritten with stale data
    refetchOnMount: false,
    enabled: false, // Prevents fetching
    staleTime: Infinity, // Prevents background refetches
  });

  const showFreeShippingOffer =
    treatments?.brandclub_checkout_free_shipping_offer?.treatment === "on";

  const subscriptionOffers = useSubscribeAndSaveOffers(
    (allCarts ?? []).flatMap((cart) =>
      cart.items.filter(isSubscriptionLine).map((item) => item.stacklineSku)
    )
  );

  const { data: cartProducts } = useAllCarts({
    select: selectCartProducts,
    // only read from cache, don't refetch to prevent race conditions when adding an item to the cart
    enabled: false,
    staleTime: Infinity,
  });

  const { data: cartItems } = useAllCarts({
    select: (data) => data.cartItems,
    // only read from cache, don't refetch to prevent race conditions when adding an item to the cart
    enabled: false,
    staleTime: Infinity,
  });
  const { mutate: updateCart } = useCartUpdates();

  const [trackCheckoutBarAction] = useTrackActions();

  const onViewBag = async () => {
    // await trackCheckoutBarAction(AnalyticsTrackingEvent.BAG_BUTTON_CLICKED, {});

    bcRouting.location.href = HOST_DOMAIN + "/mybag?action=checkout";
  };

  const onCheckout = async (allCarts: UserRetailerShoppingCart[]) => {
    const retailerIds = allCarts?.map((cart) => cart.retailerId);

    const p1 = trackCheckoutBarAction(
      AnalyticsTrackingEvent.BC_USER_REQUESTED_CHECKOUT,
      {
        cartItems: allCarts?.map((cart) => ({
          items: cart.items,
          retailerId: cart.retailerId,
        })),
        retailerIds,
        retailerId: retailerIds?.[0],
        source: "brandclub_dtc_checkout_bar",
      }
    );
    const p2 = await createShareableShoppingCart({
      retailerId: allCarts?.[0]?.retailerId as number,
      items: allCarts?.[0]?.items as ShoppingCartItem[],
    });
    const [{ shoppingCartId }] = await Promise.all([p2, p1]);
    const checkoutPath = pickCheckoutPath(treatments?.[CHECKOUT_SIMPLIFIED_FLAG]?.treatment, allCarts);
    bcRouting.location.href =
      HOST_DOMAIN +
      `${checkoutPath}?action=checkout&shareShoppingCartId=${shoppingCartId}`;
  };

  // A new frequency changes the quantity by zero.
  const handleChangeSubscription = (line: ShoppingCartItem, frequency: SubscriptionFrequency) => {
    updateCart({
      action: "add",
      items: [{ ...line, subscriptionFrequency: frequency, quantity: 0 }],
      retailerId: line.retailerId,
    });
  };

  const handleAddQuantity = (product: ShoppingCartItem) => {
    updateCart({
      action: "add",
      items: [{ ...product, quantity: 1 }],
      retailerId: product.retailerId,
    });
    trackCheckoutBarAction(AnalyticsTrackingEvent.DTC_UPDATE_ITEM_QUANTITY, {
      stacklineSku: product.stacklineSku,
      retailerId: product.retailerId,
      quantity: product.quantity,
      source: "brandclub_dtc_checkout_bar",
    });
  };

  const handleMinusQuantity = (product: ShoppingCartItem) => {
    if (product.quantity - 1 <= 0) {
      onRemove(product);
      return;
    }
    updateCart({
      action: "add",
      items: [{ ...product, quantity: -1 }],
      retailerId: product.retailerId,
    });
    trackCheckoutBarAction(AnalyticsTrackingEvent.DTC_UPDATE_ITEM_QUANTITY, {
      stacklineSku: product.stacklineSku,
      retailerId: product.retailerId,
      quantity: product.quantity,
      source: "brandclub_dtc_checkout_bar",
    });
  };

  const onRemove = (product: ShoppingCartItem) => {
    updateCart({
      action: "remove",
      items: [{ ...product, quantity: 0 }],
      retailerId: product.retailerId,
    });
  };

  const onEmptyBagAction = () => {
    const linkTo = `https://${
      domainConfig?.customShopPageUrls?.popularProductsPage ||
      domainConfig?.customShopDomainName
    }`;
    bcRouting.navigate(linkTo, "_self");
  };

  // A host asked for the checkout (`openCheckout`). It runs the drawer's own Check out on the bag as it is now.
  const checkoutRequestId = useCheckoutStart((s) => s.requestId);
  useEffect(() => {
    if (!checkoutRequestId) return;
    // Take the requests here, so a second run of this effect finds none and starts no second checkout.
    const waiting = useCheckoutStart.getState().take();
    if (!waiting.length) return;
    const answer = (outcome: CheckoutStartOutcome) => waiting.forEach((settle) => settle(outcome));
    queryClient
      .ensureQueryData(activeCartsQueryOptions())
      .then(async (data) => {
        const carts = data.userRetailerShoppingCarts.filter((c) => !!c.items?.length && !!c.retailerId);
        if (!carts.length) return answer({ status: "refused", message: BAG_EMPTY });
        await onCheckout(carts);
        answer({ status: "leaving" });
      })
      .catch((error: unknown) => {
        console.error("Checkout start failed", error);
        answer({ status: "refused", message: CHECKOUT_NOT_STARTED });
      });
    // eslint-disable-next-line -- exhaustive-deps intentionally relaxed (checkoutRequestId drives the effect)
  }, [checkoutRequestId]);

  useEffect(() => {
    if (open) {
      trackCheckoutBarAction(AnalyticsTrackingEvent.DTC_OPEN_BUYBOX, {
        source: "brandclub_dtc_checkout_bar",
      });
    }
  }, [open]);

  return (
    <Drawer
      id="cart-popover"
      open={open}
      sx={{
        width: 462,
        maxWidth: "100%",
        // Above the Concierge floating button / chat pane, which default to
        // z_index 6000 (DEFAULT_CONCIERGE_SETTINGS in @brandclub/common-ui).
        // MUI's default drawer z-index (1200) leaves the bubble floating on top.
        zIndex: 6100,
      }}
      PaperProps={{
        sx: {
          boxShadow: "none",
          borderRadius: "0",
          maxWidth: "100%",
          ...brandclubStyles.customScrollbar,
        } as SxProps<Theme>,
      }}
      anchor="right"
      disableAutoFocus
      disableScrollLock
      disableEnforceFocus
      disableRestoreFocus
      onClose={() => {
        close();
      }}
    >
      {refusal && <RefusalLine message={refusal} onDismiss={() => useDialogStore.setState({ refusal: null })} />}
      <CheckoutDrawer
        showFreeShippingOffer={showFreeShippingOffer}
        storeBrandingType={StoreBrandingType.CustomDTCStore}
        cartItems={cartItems ?? []}
        cartProducts={cartProducts}
        allCarts={allCarts}
        loading={initializingCart}
        closeDrawer={close}
        onCheckout={onCheckout}
        closeAfterCheckout={false}
        handleAddQuantity={handleAddQuantity}
        handleMinusQuantity={handleMinusQuantity}
        subscriptionOffers={subscriptionOffers}
        handleChangeSubscription={handleChangeSubscription}
        onViewBag={onViewBag}
        onEmptyBagAction={onEmptyBagAction}
        LoadingComponent={() => <CartProductLoading></CartProductLoading>}
      />
    </Drawer>
  );
};

/** Open the drawer. A refusal from an earlier add goes. */
BagDrawer.show = () => {
  useDialogStore.setState({
    open: true,
    refusal: null,
  });
};

/** Open the drawer with the sentence of a refused add at its top. */
BagDrawer.refuse = (message: string) => {
  useDialogStore.setState({ open: true, refusal: message });
};

BagDrawer.checkout = requestCheckoutStart;

export default BagDrawer;
